import type { AuthResponse, SignupProfile } from "./types";

function asString(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}

/**
 * Build Signup Request Body.
 */
export function buildSignupRequestBody(
  email: string,
  password: string,
  profile?: SignupProfile,
  captchaToken?: string
): Record<string, string> {
  const profileData = profile as Record<string, unknown> | undefined;

  return {
    email,
    password,
    full_name: profile?.full_name ?? "",
    display_name: profile?.display_name ?? "",
    phone: asString(profileData?.phone),
    location: asString(profileData?.location),
    occupation: asString(profileData?.occupation),
    signup_type: asString(profileData?.signup_type, "individual"),
    terms_accepted_at: asString(profileData?.terms_accepted_at),
    terms_version: asString(profileData?.terms_version),
    organization_name: asString(profileData?.organization_name),
    organization_size: asString(profileData?.organization_size),
    organization_domain: asString(profileData?.organization_domain),
    ...(captchaToken ? { captchaToken } : {}),
    ...(typeof profileData?.hr_invite_token === "string"
      ? { hr_invite_token: profileData.hr_invite_token.trim() }
      : {}),
  };
}

/**
 * Build Signup Error Response.
 */
export function buildSignupErrorResponse(err: unknown): AuthResponse {
  const message =
    err && typeof err === "object" && "message" in err
      ? asString((err as { message: unknown }).message, "Failed to create account")
      : "Failed to create account";

  return {
    user: null,
    session: null,
    error: { message, code: "AUTH_003" },
  };
}

export function normalizeSigninEmail(email: string): string {
  return email.trim().toLowerCase();
}

export function buildAccountLockedResponse(): AuthResponse {
  return {
    error: {
      message:
        "Account temporarily locked due to too many failed login attempts. Try again in 15 minutes.",
      code: "ACCOUNT_LOCKED",
    },
  };
}

export function buildPasswordExpiredResponse(): AuthResponse {
  return {
    error: {
      message: "Your password has expired. Please reset it to continue.",
      code: "PASSWORD_EXPIRED",
    },
  };
}

export function shouldRequireMfa(mfaEnabled: boolean): boolean {
  return mfaEnabled;
}

export function buildMfaRequiredResponse(
  user: AuthResponse["user"],
  session: AuthResponse["session"]
): AuthResponse {
  return {
    user,
    session,
    mfaRequired: true,
  };
}
